import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box, Button, Checkbox, FormControlLabel, FormHelperText, FormLabel,
  MenuItem, Radio, RadioGroup, Select, TextField,
  Typography, Divider, Alert, CircularProgress, FormControl, InputLabel,
  Chip, OutlinedInput
} from '@mui/material';
import { getFormSpecs, buildSchema } from '../../config/formsConfig';
import api from '../../api/api';

const setDeep = (obj, path, value) => {
  const parts = path.split('.');
  let cur = obj;
  for (let i = 0; i < parts.length - 1; i++) {
    if (!cur[parts[i]] || typeof cur[parts[i]] !== 'object') cur[parts[i]] = {};
    cur = cur[parts[i]];
  }
  cur[parts[parts.length - 1]] = value;
};

const COMMON_KEYS = [
  'titulo','descripcion','acceso','urgencia','horarios_preferidos',
  'fotos','presupuesto_estimado','acepta_visita','acepta_presupuesto_previo','terminos_aceptados'
];

// Ancho en % del contenedor para cada tipo de campo
const getWidth = (spec) => {
  if (['textarea','multiselect','radio','files','timeslots'].includes(spec.type)) return '100%';
  if (spec.type === 'select') return '16%';
  if (spec.type === 'number') return '20%';
  if (spec.type === 'date')   return '11%';
  if (spec.type === 'text')   return spec.key.includes('descripcion') ? '100%' : '11%';
  return '16%';
};

// Estilo común para el wrapper de cada campo
const fieldWrapper = (width) => ({
  width,
  minWidth: width === '100%' ? 0 : width === '16%' ? '160px' : width === '20%' ? '150px' : '120px',
  flexShrink: 0,
  flexGrow: width === '100%' ? 1 : 0,
  boxSizing: 'border-box',
  '@media (max-width:600px)': { width: '100%', minWidth: 0 },
});

function Field({ spec, control, errors }) {
  const err = errors?.[spec.key];
  const w = getWidth(spec);

  switch (spec.type) {

    case 'text':
    case 'number':
    case 'date':
      return (
        <Box sx={fieldWrapper(w)}>
          <Controller name={spec.key} control={control} defaultValue="" render={({ field }) => (
            <TextField
              {...field}
              label={spec.label}
              type={spec.type === 'number' ? 'number' : spec.type === 'date' ? 'date' : 'text'}
              size="small" fullWidth
              required={spec.required}
              error={!!err}
              helperText={err ? 'Campo requerido o inválido' : ''}
              InputLabelProps={spec.type === 'date' ? { shrink: true } : undefined}
            />
          )} />
        </Box>
      );

    case 'textarea':
      return (
        <Box sx={fieldWrapper(w)}>
          <Controller name={spec.key} control={control} defaultValue="" render={({ field }) => (
            <TextField
              {...field}
              label={spec.label}
              multiline rows={3}
              size="small" fullWidth
              required={spec.required}
              error={!!err}
              helperText={err ? 'Mínimo 30 caracteres' : ''}
            />
          )} />
        </Box>
      );

    case 'select':
      return (
        <Box sx={fieldWrapper(w)}>
          <Controller name={spec.key} control={control} defaultValue="" render={({ field }) => (
            <FormControl size="small" fullWidth error={!!err} required={spec.required}>
              <InputLabel>{spec.label}</InputLabel>
              <Select {...field} label={spec.label}>
                <MenuItem value=""><em>Seleccionar…</em></MenuItem>
                {spec.options?.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
              </Select>
              {err && <FormHelperText>Campo requerido</FormHelperText>}
            </FormControl>
          )} />
        </Box>
      );

    case 'multiselect':
      return (
        <Box sx={fieldWrapper(w)}>
          <Controller name={spec.key} control={control} defaultValue={[]} render={({ field }) => {
            const selected = field.value || [];
            const toggle = (opt) => {
              const next = selected.includes(opt)
                ? selected.filter(v => v !== opt)
                : [...selected, opt];
              field.onChange(next);
            };
            return (
              <FormControl fullWidth error={!!err} required={spec.required}>
                <FormLabel sx={{ fontSize: '0.8rem', color: '#163244', mb: 1, fontWeight: 600 }}>
                  {spec.label}{spec.required ? ' *' : ''}
                  {selected.length > 0 && (
                    <Typography component="span" sx={{ ml: 1, fontSize: '0.75rem', color: '#E7A432', fontWeight: 700 }}>
                      ({selected.length} seleccionado{selected.length > 1 ? 's' : ''})
                    </Typography>
                  )}
                </FormLabel>
                <Box sx={{
                  display: 'flex', flexWrap: 'wrap', gap: 1,
                  border: err ? '1px solid #d32f2f' : '1px solid #cddae4',
                  borderRadius: 2, p: 1.5, background: '#fafafa',
                }}>
                  {spec.options?.map(o => {
                    const checked = selected.includes(o);
                    return (
                      <Box
                        key={o}
                        onClick={() => toggle(o)}
                        sx={{
                          px: 1.5, py: 0.5, borderRadius: 20, cursor: 'pointer',
                          fontSize: '0.82rem', fontWeight: checked ? 700 : 400,
                          border: `1.5px solid ${checked ? '#163244' : '#cddae4'}`,
                          background: checked ? '#163244' : '#fff',
                          color: checked ? '#E7A432' : '#5c7a8a',
                          transition: 'all 0.15s',
                          userSelect: 'none',
                          '&:hover': { borderColor: '#163244', background: checked ? '#163244' : '#f0f5f8' },
                        }}
                      >
                        {checked ? '✓ ' : ''}{o}
                      </Box>
                    );
                  })}
                </Box>
                {err && <FormHelperText>Seleccioná al menos una opción</FormHelperText>}
                {selected.length > 0 && (
                  <Typography sx={{ fontSize: '0.72rem', color: '#8fa3b1', mt: 0.5 }}>
                    Seleccionados: {selected.join(', ')}
                  </Typography>
                )}
              </FormControl>
            );
          }} />
        </Box>
      );

    case 'radio':
      return (
        <Box sx={fieldWrapper(w)}>
          <Controller name={spec.key} control={control} defaultValue="" render={({ field }) => (
            <FormControl component="fieldset" error={!!err} required={spec.required} fullWidth>
              <FormLabel sx={{ fontSize: '0.8rem', color: '#163244', mb: 0.5 }}>
                {spec.label}
              </FormLabel>
              <RadioGroup {...field} row sx={{ gap: 1 }}>
                {spec.options?.map(o => (
                  <FormControlLabel key={o} value={o}
                    control={<Radio size="small" />}
                    label={<Typography variant="body2">{o}</Typography>} />
                ))}
              </RadioGroup>
              {err && <FormHelperText>Seleccioná una opción</FormHelperText>}
            </FormControl>
          )} />
        </Box>
      );

    case 'checkbox':
      return (
        <Controller name={spec.key} control={control} defaultValue={spec.defaultValue ?? false} render={({ field }) => (
          <FormControlLabel
            control={<Checkbox {...field} checked={!!field.value} size="small" />}
            label={<Typography variant="body2">{spec.label}{spec.required ? ' *' : ''}</Typography>}
          />
        )} />
      );

    case 'timeslots':
      return (
        <Box sx={fieldWrapper(w)}>
          <Controller name={spec.key} control={control} defaultValue="" render={({ field }) => (
            <TextField {...field} label={spec.label}
              placeholder="Ej: Lun–Vie 9–12, Sáb 14–18"
              size="small" fullWidth />
          )} />
        </Box>
      );

    case 'files':
      return (
        <Box sx={fieldWrapper(w)}>
          <Controller name={spec.key} control={control} defaultValue={null} render={({ field: { onChange } }) => (
            <Box>
              <FormLabel sx={{ display: 'block', mb: 0.5, fontSize: '0.8rem', color: '#163244' }}>
                {spec.label}
              </FormLabel>
              <input type="file" multiple accept="image/*"
                onChange={(e) => onChange(e.target.files)}
                style={{ fontSize: '0.85rem' }} />
            </Box>
          )} />
        </Box>
      );

    default:
      return null;
  }
}

// Layout: flexbox wrapping, cada campo controla su propio ancho
function FieldGrid({ specs, control, errors }) {
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, width: '100%' }}>
      {specs.filter(s => s.type !== 'checkbox').map(spec => (
        <Field key={spec.key} spec={spec} control={control} errors={errors} />
      ))}
    </Box>
  );
}

export default function DynamicForm({ serviceKey, onSubmitSuccess }) {
  const schema = React.useMemo(() => buildSchema(serviceKey), [serviceKey]);
  const specs  = React.useMemo(() => getFormSpecs(serviceKey), [serviceKey]);

  const { control, handleSubmit, formState: { errors, isSubmitting }, reset, setValue } = useForm({
    resolver: zodResolver(schema),
  });

  const [submitError, setSubmitError] = React.useState(null);
  const [submitted, setSubmitted]     = React.useState(false);
  const [geoLoading, setGeoLoading]   = React.useState(false);
  const [geoError,   setGeoError]     = React.useState('');

  // Detectar si el formulario tiene campos de ubicación
  const hasUbicacion = specs.some(f => f.key.startsWith('ubicacion.'));

  const handleGeolocate = async () => {
    setGeoError('');
    if (!navigator.geolocation) {
      setGeoError('Tu dispositivo no soporta geolocalización.');
      return;
    }
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { latitude, longitude } = pos.coords;
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json&addressdetails=1`,
            { headers: { 'Accept-Language': 'es' } }
          );
          const data = await res.json();
          const addr = data.address || {};

          // Mapear campos de Nominatim a los campos del formulario
          const direccion = [
            addr.road || addr.pedestrian || addr.footway || '',
            addr.house_number || '',
          ].filter(Boolean).join(' ') || data.display_name?.split(',')[0] || '';

          const ciudad = addr.city || addr.town || addr.village || addr.municipality || '';
          const provincia = addr.state || addr.region || '';

          if (direccion) setValue('ubicacion.direccion', direccion);
          if (ciudad)    setValue('ubicacion.ciudad',    ciudad);
          if (provincia) setValue('ubicacion.provincia', provincia);

          if (!direccion && !ciudad) {
            setGeoError('No se pudo obtener la dirección. Completala manualmente.');
          }
        } catch {
          setGeoError('Error al obtener la dirección. Intentá de nuevo.');
        } finally {
          setGeoLoading(false);
        }
      },
      (err) => {
        setGeoLoading(false);
        if (err.code === 1) setGeoError('Permiso de ubicación denegado. Completá la dirección manualmente.');
        else setGeoError('No se pudo obtener tu ubicación. Completá la dirección manualmente.');
      },
      { timeout: 8000 }
    );
  };

  const specificSpecs  = specs.filter(f => !COMMON_KEYS.includes(f.key) && !f.key.startsWith('ubicacion.'));
  const mainCommonSpecs = specs.filter(f =>
    (COMMON_KEYS.includes(f.key) || f.key.startsWith('ubicacion.')) && f.type !== 'checkbox'
  );
  const checkboxSpecs = specs.filter(f => COMMON_KEYS.includes(f.key) && f.type === 'checkbox');

  const onSubmit = async (raw) => {
    setSubmitError(null);
    const common = {};
    const serviceSpecific = {};
    specs.forEach(f => {
      const val = raw[f.key];
      if (val === undefined) return;
      if (f.key.startsWith('ubicacion.')) setDeep(common, f.key, val);
      else if (COMMON_KEYS.includes(f.key)) common[f.key] = val;
      else serviceSpecific[f.key] = val;
    });
    try {
      const { data } = await api.post('/api/service-requests',
        { serviceKey, common, serviceSpecific }
      );
      // Llamar onSubmitSuccess ANTES de mutar estado local.
      // ServiceRequestPage maneja la navegación — no usamos setSubmitted
      // para no generar una re-renderización que compita con el navigate del padre.
      if (onSubmitSuccess) {
        await onSubmitSuccess(data.id);
      } else {
        // Fallback si se usa DynamicForm sin padre que navegue
        setSubmitted(true);
        reset();
      }
    } catch (err) {
      setSubmitError(err.response?.data?.error || 'Ocurrió un error al enviar la solicitud.');
    }
  };

  if (submitted) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <Typography variant="h5" sx={{ color: '#163244', mb: 1 }}>Solicitud enviada</Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          Nos pondremos en contacto pronto con prestadores disponibles.
        </Typography>
        <Button variant="contained"
          sx={{ backgroundColor: '#E7A432', color: '#163244' }}
          onClick={() => setSubmitted(false)}>
          Hacer otra solicitud
        </Button>
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
      {submitError && <Alert severity="error" sx={{ mb: 3 }}>{submitError}</Alert>}

      {specificSpecs.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ color: '#163244', mb: 2, textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.72rem' }}>
            Detalles del servicio
          </Typography>
          <FieldGrid specs={specificSpecs} control={control} errors={errors} />
          <Divider sx={{ mt: 3 }} />
        </Box>
      )}

      <Box sx={{ mt: 3, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="subtitle2" sx={{ color: '#163244', textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.72rem' }}>
            Información general
          </Typography>
          {hasUbicacion && (
            <Button
              size="small"
              variant="outlined"
              onClick={handleGeolocate}
              disabled={geoLoading}
              startIcon={geoLoading ? <CircularProgress size={14} /> : <span>📍</span>}
              sx={{ borderColor: '#163244', color: '#163244', fontSize: '0.75rem', py: 0.5,
                '&:hover': { backgroundColor: '#f0f5f8' } }}
            >
              {geoLoading ? 'Obteniendo ubicación...' : 'Usar mi ubicación'}
            </Button>
          )}
        </Box>
        {geoError && (
          <Alert severity="warning" sx={{ mb: 2, py: 0.5 }} onClose={() => setGeoError('')}>
            {geoError}
          </Alert>
        )}
        <FieldGrid specs={mainCommonSpecs} control={control} errors={errors} />
      </Box>

      {checkboxSpecs.length > 0 && (
        <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {checkboxSpecs.map(spec => (
            <Field key={spec.key} spec={spec} control={control} errors={errors} />
          ))}
        </Box>
      )}

      <Button type="submit" variant="contained" fullWidth disabled={isSubmitting}
        sx={{ mt: 4, py: 1.4, backgroundColor: '#163244', color: '#fff',
          borderRadius: 2, fontSize: '0.95rem', '&:hover': { backgroundColor: '#0e2233' } }}>
        {isSubmitting ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Enviar solicitud'}
      </Button>
    </Box>
  );
}

export { DynamicForm };
